import {
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";

//Components
import { Tabs, Tab, Box, AppBar } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import Dashboard from "./Dashboard";
import WorkQueue from "./WorkQueue";

//Contexts
import { IncidentsContext } from "../Contexts/IncidentsContext";
import { INCIDENT_ACTIONS } from "../Actions/IncidentActions";

//Styling
import { makeStyles, withStyles } from "@material-ui/core/styles";

//Fetching
import {
  useLazyQuery,
  useMutation,
  useQuery,
  useApolloClient,
} from "@apollo/client";
import { GET_INITIAL_CALL_INFO } from "../Queries/GetInitialCallInfo";
import { GET_PERSON } from "../Queries/GetPerson";
import { GET_TICKET } from "../Queries/GetTicket";
import { OFFICER_WORK_QUEUE_QUERY } from "../Queries/OfficerWorkQueueQuery";
import { SUPERVISOR_WORK_QUEUE_QUERY } from "../Queries/SupervisorWorkQueueQuery";

//Utils
import PropTypes from "prop-types";
import {
  returnPeopleInIncident,
  returnOfficersInIncident,
  findPrimaryOfficer,
  ticketStatusHash,
  useLocalStorage,
  localGetData,
  localStoreData,
} from "../utils/utils";
import PersonDetailWrapper from "./PersonDetail/PersonDetail";
import { PERSON_ACTIONS } from "../Actions/PersonActions";
import { TICKET_ACTIONS } from "../Actions/TicketActions";
import TicketAndWarning from "./TicketAndWarning/TicketAndWarning";
import { UserContext } from "../Contexts/UserContext";
import { checkPermission } from "../utils/Roles";

import QueryReturnTabWrapper from "./QueryReturnTab";
import LogsTabWrapper from "./LogsTab";
import DashboardNavBar from "./DashboardNavBar";
import { DashboardContext } from "../Contexts/DashboardContext";
import { GET_CHAT_SERVER_URL } from "../Queries/GetChatServerUrl";
import VehicleDetail from "./VehicleDetail/VehicleDetail";
import { VEHICLE_ACTIONS } from "../Actions/VehicleActions";
import Bulletins from "./Bulletins";
import { RESET_SESSION_TIMEOUT } from "../Mutations/ResetSessionTimeout";
import { GET_CORE_SERVER_URL } from "../Queries/GetCoreServerUrl";
import { GET_VEHICLE_INCIDENT_SPECIFIC } from "../Queries/GetVehicleIncidentSpecific";

import { NEW_INCIDENT_MUTATION } from "../Mutations/NewIncidentMutation";
import { MutationContext } from "../Contexts/MutationContext";
import { ACTIVE_INCIDENTS_ACTIONS } from "../Actions/ActiveIncidentsActions";
import IncidentSubscriptionContextProvider from "../Contexts/IncidentSubscriptionContext";
import DispatchedModal from "./DispatchedModal/DispatchedModal";
import { throttleFunction } from "../utils/throttleFunction";
import { checkTables } from "../IndexedDb/tableFunctions";
import { db, indexedDbTableNames } from "../IndexedDb/database";
import useUpdateOpenArray from "../CustomHooks/useUpdateOpenArray";
import IncidentPanelsWrapper from "./IncidentPanelsWrapper";
import { SingleIncidentProvider } from "../Contexts/SingleIncidentContext";
import { ADD_OR_UPDATE_PERSON_IN_INCIDENT } from "../Mutations/AddOrUpdatePersonInIncident";

import moment from "moment";
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={0}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.paper,
  },
  hover: {
    color: "black",
    "&:hover": {
      color: "#d9833b !important",
    },
  },
}));

const AntTabs = withStyles({
  root: {
    borderBottom: "none",
  },
  indicator: {
    display: "none",
  },
})(Tabs);

const AntTab = withStyles(theme => ({
  root: {
    backgroundColor: "lightgrey",
    "&:hover": {
      color: "#d9833b",
      opacity: 1,
    },
    "&$selected": {
      color: "white",
      backgroundColor: "rgb(48, 62, 79)",
      fontWeight: theme.typography.fontWeightMedium,
      border: "none",
      "& > span > div > svg": {
        color: "white",
        "&:hover": {
          color: "#d9833b !important",
        },
      },
    },
    borderTopLeftRadius: "10px",
    borderTopRightRadius: "10px",
    border: "1px solid rgb(48, 62, 79)",
    borderBottom: "none",
    marginRight: "5px",
  },
  selected: {},
}))(props => <Tab disableRipple {...props} />);

export default function TabsWrapper({
  subscriptionServer,
  setPopper,
  pausePolling,
  setPausePolling,
  timestamp,
  socketConnected,
  ...props
}) {
  const classes = useStyles();
  const [value, setValue] = useState(0);
  const { updateIncidentDetail } = useContext(MutationContext);
  const { user, responseErrorHandler, onlineState, dispatched, setDispatched } =
    useContext(UserContext);
  const { activeIncidents, activeIncidentDispatch } =
    useContext(DashboardContext);
  const {
    incidents,
    incidentDispatch,
    people,
    personDispatch,
    focusTab,
    setFocusTab,
    incidentPanel,
    setIncidentPanel,
    tickets,
    ticketDispatch,
    vehicles,
    vehicleDispatch,
  } = useContext(IncidentsContext);
  const chatWindowRef = useRef(null);
  const coreWindowRef = useRef(null);

  const [localSwitchTab, setLocalSwitchTab] = useState(false);
  const ticketToFocusRef = useRef(null);
  const { ADD_NEW_INCIDENT } = ACTIVE_INCIDENTS_ACTIONS;
  const {
    CLOSE_INCIDENT,
    SET_INITIAL_CALL_INFO,
    UPDATE_INCIDENT_DETAIL,
    MERGE_PIS,
  } = INCIDENT_ACTIONS;
  const { CLOSE_PERSON, SET_PERSON_INFO } = PERSON_ACTIONS;
  const { CLOSE_TICKET, SET_TICKET_INFO } = TICKET_ACTIONS;
  const { CLOSE_VEHICLE, SET_VEHICLE_INFO } = VEHICLE_ACTIONS;

  const tabWrapperRef = useRef(null);
  const [dashSize, setDashSize] = useState({
    width: window.innerWidth,
    height: undefined,
  });

  const client = useApolloClient();

  const STATIC_TABS = [
    {
      name: "Dashboard",
      index: 0,
    },
    {
      name: "Work Queue",
      index: 1,
    },
    {
      name: "Query Returns",
      index: 2,
    },
    {
      name: "Logs",
      index: 3,
    },
  ];
  const [resetSessionTimeout] = useMutation(RESET_SESSION_TIMEOUT, {
    context: { serializationKey: "incidentQueue" },
  });

  const handleUpdateOpenLocalStorage = useUpdateOpenArray(user);

  //This will check the timestamp and activity every second but
  //because other requests can update the timestamp, only fire the
  //resetSessionTimeout mutation if the last timestamp is 10 or more seconds old
  //and the user is active
  useEffect(() => {
    const timer = setInterval(() => {
      if (active.current) {
        const now = Math.floor(Date.now() / 1000);
        if (now - timestamp >= 10) {
          active.current = false;
          resetSessionTimeout({
            variables: { input: { originatorId: user?.id } },
          });
        }
      }
    }, 1000);
    // clearing interval
    return () => clearInterval(timer);
  });

  const active = useRef(false);

  useEffect(() => {
    const makeActive = () => {
      if (!active.current) {
        active.current = true;
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        makeActive();
      }
    };

    window.addEventListener("keydown", makeActive);
    window.addEventListener("mousedown", makeActive);
    window.addEventListener("mousemove", makeActive);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.addEventListener("keydown", makeActive);
      window.addEventListener("mousedown", makeActive);
      window.addEventListener("mousemove", makeActive);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [active]);

  const [getChatServerUrl] = useLazyQuery(GET_CHAT_SERVER_URL, {
    fetchPolicy: "no-cache",
    context: { serializationKey: "incidentQueue" },
    onCompleted: data => {
      if (
        data &&
        data.getChatServerUrl &&
        data.getChatServerUrl.url &&
        data.getChatServerUrl.args
      ) {
        const url =
          data.getChatServerUrl.url +
          "/?chat_arg=" +
          data.getChatServerUrl.args;
        chatWindowRef.current = window.open(url, "chatWindow");
      }
    },
  });
  const [getCoreServerUrl] = useLazyQuery(GET_CORE_SERVER_URL, {
    fetchPolicy: "no-cache",
    context: { serializationKey: "incidentQueue" },
    onCompleted: data => {
      if (
        data &&
        data.getCoreServerUrl &&
        data.getCoreServerUrl.url &&
        data.getCoreServerUrl.args
      ) {
        const url =
          data.getCoreServerUrl.url +
          "/?encrypted_args=" +
          data.getCoreServerUrl.args;
        coreWindowRef.current = window.open(url, "coreWindow");
      }
    },
  });

  const [updatePis] = useMutation(ADD_OR_UPDATE_PERSON_IN_INCIDENT, {
    onCompleted: res => {
      if (res) {
        const pis = res[Object.keys(res)[0]]?.personIncidentSpecific;
        incidentDispatch({
          type: MERGE_PIS,
          payload: { incidentId: pis?.incident?.id, value: pis },
        });
      }
    },
  }); // Initialize the mutation

  const totalTabs = useMemo(() => {
    return (
      STATIC_TABS.length +
      (incidents?.length || 0) +
      (people?.length || 0) +
      (tickets?.length || 0) +
      vehicles?.length -
      1
    );
  }, [
    STATIC_TABS.length,
    incidents?.length,
    people?.length,
    tickets?.length,
    vehicles?.length,
  ]);

  const handleTabChange = useCallback(
    (event, newValue) => {
      try {
        if (newValue >= 0 && typeof newValue === "number") {
          setValue(newValue);
          setFocusTab(prevState => {
            localStoreData(prevState, `${user?.id}-last-tab`);
            localStoreData(newValue, `${user?.id}-current-tab`);
            return newValue;
          });
        } else {
          throw new Error("Out of range value");
        }
      } catch (error) {
        console.error(error.message);
        let validValue = newValue;
        while (validValue > 0) {
          validValue--;
          if (validValue >= 0 && validValue <= totalTabs) {
            setFocusTab(validValue);
            setValue(validValue);
            localStoreData(validValue, `${user?.id}-current-tab`);
            break;
          }
        }
      }
    },
    [setFocusTab, totalTabs, user?.id]
  );

  const handleFocusOnTabClosing = () => {
    //minus one here because one is being closed
    let tab = localGetData(`${user?.id}-last-tab`) || 0;
    //minus one here for the index reference
    tab = tab > totalTabs - 1 ? 0 : tab;
    setFocusTab(tab);
    setValue(tab);
  };

  const handleCloseIncident = (incident, target) => {
    if (
      onlineState
        ? true
        : window.confirm(
            `You are currently offline and may not be able to re-open this tab. Are you sure you want to close ${incident.incidentNumber}?`
          )
    ) {
      let x = incidentPanel;
      delete x[incident.id];
      setIncidentPanel(x);
      if (target > value) {
        incidentDispatch({ type: CLOSE_INCIDENT, payload: incident.id });
      } else {
        value > 0 && handleFocusOnTabClosing();
        incidentDispatch({ type: CLOSE_INCIDENT, payload: incident.id });
      }
      subscriptionServer.unsubscribeToIncident(incident?.id);
      handleUpdateOpenLocalStorage(incident.id, "close", "incidents");
      return;
    } else {
      return;
    }
  };

  const handleClosePerson = (person, target) => {
    if (
      onlineState
        ? true
        : window.confirm(
            `You are currently offline and may not be able to re-open this tab. Are you sure you want to close ${person?.personalAttribute?.lastName}?`
          )
    ) {
      if (target > value) {
        personDispatch({ type: CLOSE_PERSON, payload: { id: person?.id } });
      } else {
        value > 0 && handleFocusOnTabClosing();
        personDispatch({ type: CLOSE_PERSON, payload: { id: person?.id } });
      }
      handleUpdateOpenLocalStorage(person.id, "close", "people");
    }
  };

  const handleCloseTicket = (ticket, target) => {
    if (
      onlineState
        ? true
        : window.confirm(
            `You are currently offline and may not be able to re-open this tab. Are you sure you want to close Ticket ${ticket?.ticketNumber}?`
          )
    ) {
      if (target > value) {
        ticketDispatch({ type: CLOSE_TICKET, payload: { id: ticket?.id } });
      } else {
        value > 0 && handleFocusOnTabClosing();
        ticketDispatch({ type: CLOSE_TICKET, payload: { id: ticket?.id } });
      }
      handleUpdateOpenLocalStorage(ticket.id, "close", "tickets");
    }
  };

  const handleCloseVehicle = (vis, target) => {
    if (
      onlineState
        ? true
        : window.confirm(
            `You are currently offline and may not be able to re-open this tab. Are you sure you want to close ${vis?.vehicle?.vehicleMake?.name} ${vis?.vehicle?.vehicleModel?.name}?`
          )
    ) {
      if (target > value) {
        vehicleDispatch({ type: CLOSE_VEHICLE, payload: { id: vis?.id } });
      } else {
        value > 0 && handleFocusOnTabClosing();
        vehicleDispatch({ type: CLOSE_VEHICLE, payload: { id: vis?.id } });
      }
      handleUpdateOpenLocalStorage(vis.id, "close", "vehicles");
    }
  };

  const [getCallInfo] = useLazyQuery(GET_INITIAL_CALL_INFO, {
    variables: { originatorId: user?.id },
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: "network-only",
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        incidentDispatch({ type: SET_INITIAL_CALL_INFO, payload: data });
        handleIncidentOpening(data.initialCallInfo.id, localSwitchTab);
      }
    },
  });

  const [getCallInfoExpandFrame] = useLazyQuery(GET_INITIAL_CALL_INFO, {
    variables: { originatorId: user?.id },
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: "network-only",
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        incidentDispatch({ type: SET_INITIAL_CALL_INFO, payload: data });
      }
    },
  });

  const [getCallInfoNoFocus] = useLazyQuery(GET_INITIAL_CALL_INFO, {
    variables: { originatorId: user?.id },
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: "network-only",
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        incidentDispatch({ type: SET_INITIAL_CALL_INFO, payload: data });
        handleIncidentOpening(data.initialCallInfo.id, localSwitchTab);
        setPopper({
          open: true,
          content: "Incident loaded in background",
          severity: "info",
        });
      }
    },
  });

  const { refetch } = useQuery(GET_INITIAL_CALL_INFO, {
    skip: true,
    fetchPolicy: "network-only",
    context: {
      serializationKey: "incidentQueue",
    },
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
      }
    },
  });

  const handleIncidentOpening = (incidentId, switchTab = true) => {
    let index = incidents.findIndex(
      i => i.id === incidentId || i.uuid === incidentId
    );
    if (index > -1) {
      if (switchTab) {
        handleTabChange(null, index + STATIC_TABS.length || 0);
      } else {
        setPopper({
          open: true,
          content: "Incident already open in background",
          severity: "info",
        });
      }
    } else {
      setLocalSwitchTab(switchTab);
      if (switchTab) {
        getCallInfo({ variables: { incidentId: incidentId } });
      } else {
        getCallInfoNoFocus({ variables: { incidentId: incidentId } });
      }
      handleUpdateOpenLocalStorage(incidentId, "open", "incidents");
    }
  };

  useEffect(() => {
    const reopenIncidents = async arr => {
      arr.map(async (i, index) => {
        if (!i.includes("Offline_")) {
          const res = await refetch({ incidentId: i, originatorId: user?.id });
          if (res?.data) {
            incidentDispatch({
              type: SET_INITIAL_CALL_INFO,
              payload: res?.data,
            });
            if (index === arr.length - 1) {
              handleTabChange(
                null,
                localGetData(`${user?.id}-current-tab`) || 0
              );
            }
          }
        }
      });
    };
    const reopenTickets = async arr => {
      for (const ticketId of arr) {
        await handleTicketOpening(ticketId, false);
      }
    };
    const reopenPeople = async arr => {
      for (const personId of arr) {
        await handlePersonOpening(personId, false);
      }
    };

    const reopenVehicles = async arr => {
      for (const vehicleId of arr) {
        await handleVehicleOpening(vehicleId, false);
      }
    };
    if (user?.id) {
      const openIncidents = localGetData(`${user?.id}-open-incidents`) || [];
      const openTickets = localGetData(`${user?.id}-open-tickets`) || [];
      const openPeople = localGetData(`${user?.id}-open-people`) || [];
      const openVehicles = localGetData(`${user?.id}-open-vehicles`) || [];

      if (user?.id && openIncidents?.length > 0) {
        reopenIncidents(openIncidents);
      }
      if (openTickets?.length > 0) {
        reopenTickets(openTickets);
      }
      if (openPeople?.length > 0) {
        reopenPeople(openPeople);
      }
      if (openVehicles?.length > 0) {
        reopenVehicles(openVehicles);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const [getPersonInfo] = useLazyQuery(GET_PERSON, {
    variables: { originatorId: user?.id },
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: "network-only",
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        personDispatch({ type: SET_PERSON_INFO, payload: data });
        handlePersonOpening(data?.getPerson?.id, true);
      }
    },
  });

  const handlePersonOpening = async (personId, switchTab = true) => {
    let index = people.findIndex(i => i.id === personId || i.uuid === personId);
    if (index > -1) {
      if (switchTab) {
        setFocusTab(prevState => {
          localStoreData(prevState, `${user?.id}-last-tab`);
          return index + (incidents?.length || 0) + STATIC_TABS.length;
        });
      }
    } else {
      await getPersonInfo({ variables: { personId: personId } });
      handleUpdateOpenLocalStorage(personId, "open", "people");
    }
  };

  const [getTicketInfo] = useLazyQuery(GET_TICKET, {
    variables: { originatorId: user?.id },
    fetchPolicy: "network-only",
    context: { serializationKey: "incidentQueue" },
    onError: error => {
      ticketToFocusRef.current = null;
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        if (data.getTicket) {
          ticketDispatch({ type: SET_TICKET_INFO, payload: data });
        } else {
          ticketToFocusRef.current = null;
        }
      } else {
        ticketToFocusRef.current = null;
      }
    },
  });

  const handleTicketOpening = async (ticketId, switchTab = true) => {
    let index = tickets.findIndex(
      i => i.id === ticketId || i.uuid === ticketId
    );
    if (index > -1) {
      if (switchTab) {
        const previousTabsLength =
          (incidents?.length || 0) + (people?.length || 0) + STATIC_TABS.length;
        setFocusTab(prevState => {
          localStoreData(prevState, `${user?.id}-last-tab`);
          handleTabChange(null, index + previousTabsLength);
          return index + previousTabsLength;
        });
        return;
      }
    } else {
      if (switchTab) ticketToFocusRef.current = ticketId;
      setLocalSwitchTab(switchTab);
      await getTicketInfo({ variables: { ticketId: ticketId } });
      handleUpdateOpenLocalStorage(ticketId, "open", "tickets");
      return;
    }
  };

  useEffect(() => {
    const ticketId = ticketToFocusRef.current;
    if (ticketId == null) return;

    const index = tickets.findIndex(
      ticket => ticket.id === ticketId || ticket.uuid === ticketId
    );
    if (index < 0) return;

    ticketToFocusRef.current = null;
    handleTabChange(
      null,
      index + STATIC_TABS.length + (incidents?.length || 0) + (people?.length || 0)
    );
  }, [tickets, incidents?.length, people?.length, handleTabChange]);

  const [getVis] = useLazyQuery(GET_VEHICLE_INCIDENT_SPECIFIC, {
    variables: { originatorId: user?.id },
    fetchPolicy: "network-only",
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        vehicleDispatch({ type: SET_VEHICLE_INFO, payload: data });
        handleVehicleOpening(data.getVehicleIncidentSpecific.id, false);
      }
    },
  });

  const handleVehicleOpening = async (visId, switchTab = true) => {
    if (!visId) {
      console.error("No vehicleIncidentSpecific found for this vehicle");
    }
    let index = vehicles.findIndex(i => i.id === visId || i.uuid === visId);
    if (index > -1) {
      if (switchTab) {
        setFocusTab(prevState => {
          localStoreData(prevState, `${user?.id}-last-tab`);
          return (
            index +
            (incidents?.length || 0) +
            (people?.length || 0) +
            (tickets?.length || 0) +
            STATIC_TABS.length
          );
        });
      }
    } else {
      await getVis({ variables: { vehicleIncidentSpecificId: visId } });
      handleUpdateOpenLocalStorage(visId, "open", "vehicles");
    }
  };

  const [
    getOfficerWorkQueue,
    {
      data: officerWorkQueueData,
      loading: officerWorkQueueLoading,
      error: officerWorkQueueError,
    },
  ] = useLazyQuery(OFFICER_WORK_QUEUE_QUERY, {
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: "network-only",
    variables: { employeeId: user?.id, originatorId: user?.id },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        // officerWorkQueueDispatch({ type: 'initialize', payload: data?.officerWorkQueue })
      }
    },
  });

  const [
    getSupervisorWorkQueue,
    {
      data: supervisorWorkQueueData,
      loading: supervisorWorkQueueLoading,
      error: supervisorWorkQueueError,
    },
  ] = useLazyQuery(SUPERVISOR_WORK_QUEUE_QUERY, {
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: "network-only",
    variables: { employeeId: user?.id, originatorId: user?.id },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        // supervisorWorkQueueDispatch({ type: 'initialize', payload: data?.supervisorWorkQueue })
      }
    },
  });

  const handleOpenChat = () => {
    if (chatWindowRef.current && !chatWindowRef.current.closed) {
      window.open("", "chatWindow");
    } else {
      getChatServerUrl({
        variables: { originatorId: user?.id, officerId: user?.id },
      });
    }
  };
  const handleOpenCore = () => {
    if (coreWindowRef.current && !coreWindowRef.current.closed) {
      coreWindowRef.current?.focus();
    } else {
      getCoreServerUrl({
        variables: { originatorId: user?.id, officerId: user?.id },
      });
    }
  };

  const [createNewIncident] = useMutation(NEW_INCIDENT_MUTATION, {
    context: { serializationKey: "incidentQueue" },
    onCompleted: data => {
      if (data) {
        const incident = data[Object.keys(data)[0]].incident;
        responseErrorHandler(data);
        activeIncidentDispatch({ type: ADD_NEW_INCIDENT, payload: incident });
        handleIncidentOpening(incident.id, true);
        //update incident detail with mutation and dispatch for occurredFrom and occurredTo
        incidentDispatch({
          type: UPDATE_INCIDENT_DETAIL,
          payload: {
            incidentId: incident?.id,
            property: "occurredFrom",
            value: moment
              .utc(incident?.callTime, "YYYY-MM-DD HH:mm:ss UTC")
              .local(),
          },
        });
        incidentDispatch({
          type: UPDATE_INCIDENT_DETAIL,
          payload: {
            incidentId: incident?.id,
            property: "occurredTo",
            value: moment
              .utc(incident?.callTime, "YYYY-MM-DD HH:mm:ss UTC")
              .local(),
          },
        });
        updateIncidentDetail({
          variables: {
            input: {
              originatorId: user?.id,
              incidentId: incident?.id,
              incidentDetailId: incident?.incidentDetail?.id,
              occurredFrom: incident?.callTime,
              occurredTo: incident?.callTime,
            },
          },
          context: {
            serializationKey: "incidentQueue",
          },
        });
      }
    },
  });

  const throttledCreateNewIncident = throttleFunction(createNewIncident);

  useEffect(() => {
    function handleResize() {
      tabWrapperRef.current &&
        setDashSize({
          width: tabWrapperRef.current.offsetWidth,
          height: tabWrapperRef.current.offsetHeight,
        });
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    localSwitchTab && focusTab && setValue(focusTab);
    if (focusTab === 1) {
      getOfficerWorkQueue();
      getSupervisorWorkQueue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTab, localSwitchTab]);

  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    function handleResize() {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [bulletinToggle, setBulletinToggle] = useState(false);
  const bulletinSize = {
    true: 240,
    false: 30,
  };
  const [userPreferences, setUserPreferences] = useLocalStorage(
    `${user?.id}-user-preferences`,
    {},
    false
  );
  const dashboardNavBarRef = useRef(null);
  const tabsRef = useRef(null);

  const _isMounted = useRef(true); // Initial value _isMounted = true

  useEffect(() => {
    return () => {
      // ComponentWillUnmount in Class Component
      _isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    async function asyncCheckTable() {
      // You can await here
      // console.log('checking IndexedDb tables')
      await checkTables(indexedDbTableNames, db, client);
      // ...
    }
    _isMounted.current && onlineState && asyncCheckTable();
  }, [_isMounted, onlineState, client]);

  return (
    <div style={{ display: "flex", direction: "row" }}>
      <Bulletins
        handleDrawerClose={() => setBulletinToggle(prevState => !prevState)}
        open={bulletinToggle}
        screenSize={screenSize}
        // height={height}
      />
      <div
        id={"tabs-wrapper"}
        style={{ width: `calc(100% - ${bulletinSize[bulletinToggle]}px)` }}
        className={classes.root}
        ref={tabWrapperRef}
      >
        <AppBar
          ref={tabsRef}
          position={
            userPreferences?.stickyTabs === "true" ? "sticky" : "static"
          }
          style={{ zIndex: 3 }}
          color="default"
        >
          <AntTabs
            variant="scrollable"
            scrollButtons="auto"
            value={value}
            onChange={handleTabChange}
            aria-label="simple tabs example"
          >
            {STATIC_TABS.map(tab => {
              return (
                <AntTab
                  key={"static-ant-tab-" + tab.index}
                  data-cy={"Dashboard-tab"}
                  id={"Dashboard-tab"}
                  label={
                    <div
                      key={"static-div-" + tab.index}
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      {tab.name}
                    </div>
                  }
                  {...a11yProps(tab.index)}
                />
              );
            })}
            {/* Generalize to hold all types of tabs */}
            {incidents.map((i, idx) => {
              return (
                <AntTab
                  data-cy={"tab-" + idx + STATIC_TABS.length}
                  key={idx + STATIC_TABS.length}
                  disabled={i.incidentNumber ? false : true}
                  id={i.id}
                  label={
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      {i.incidentNumber ? i.incidentNumber : "Loading"}
                      <CloseIcon
                        onClick={e => {
                          e.stopPropagation();
                          handleCloseIncident(i, idx + STATIC_TABS.length);
                        }}
                        className={classes.hover}
                      />
                    </div>
                  }
                  {...a11yProps(idx + STATIC_TABS.length)}
                />
              );
            })}
            {people.map((person, idx) => (
              <AntTab
                data-cy={
                  "tab-" + idx + STATIC_TABS.length + (incidents?.length || 0)
                }
                key={idx + STATIC_TABS.length + (incidents?.length || 0)}
                disabled={false}
                id={person.id}
                label={
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    {person?.personalAttribute?.lastName}
                    {person?.personalAttribute?.firstName
                      ? ", " + person?.personalAttribute?.firstName
                      : ""}
                    <CloseIcon
                      onClick={e => {
                        e.stopPropagation();
                        handleClosePerson(
                          person,
                          idx + STATIC_TABS.length + (incidents?.length || 0)
                        );
                      }}
                      className={classes.hover}
                    />
                  </div>
                }
                {...a11yProps(idx + STATIC_TABS.length)}
              />
            ))}
            {tickets.map((ticket, idx) => (
              <AntTab
                data-cy={
                  "tab-" +
                  idx +
                  STATIC_TABS.length +
                  (incidents?.length || 0) +
                  (people?.length || 0)
                }
                key={
                  idx +
                  STATIC_TABS.length +
                  (incidents?.length || 0) +
                  (people?.length || 0)
                }
                disabled={false}
                id={ticket.id}
                label={
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      Ticket: {ticket?.ticketNumber}
                      <span
                        style={{
                          color:
                            ticket?.currentStatus >= 4 ? "green" : "inherit",
                        }}
                        className="MuiTab-root"
                      >
                        Status: {ticketStatusHash[ticket.currentStatus || 1]}
                      </span>
                    </div>
                    <CloseIcon
                      onClick={e => {
                        e.stopPropagation();
                        handleCloseTicket(
                          ticket,
                          idx +
                            STATIC_TABS.length +
                            (incidents?.length || 0) +
                            (people?.length || 0)
                        );
                      }}
                      className={classes.hover}
                    />
                  </div>
                }
                {...a11yProps(idx + STATIC_TABS.length)}
              />
            ))}
            {vehicles.map((vis, idx) => (
              <AntTab
                data-cy={
                  "tab-" +
                  idx +
                  STATIC_TABS.length +
                  (incidents?.length || 0) +
                  (people?.length || 0) +
                  (tickets?.length || 0)
                }
                key={
                  idx +
                  STATIC_TABS.length +
                  (incidents?.length || 0) +
                  (people?.length || 0) +
                  (tickets?.length || 0)
                }
                disabled={false}
                id={vis.id}
                label={
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    Vehicle:{" "}
                    {vis?.vehicle?.vehicleModel?.name || "Unknown Model"},{" "}
                    {vis?.vehicle?.vehicleMake?.name || "Unknown Make"}
                    <CloseIcon
                      onClick={e => {
                        e.stopPropagation();
                        handleCloseVehicle(
                          vis,
                          idx +
                            STATIC_TABS.length +
                            (incidents?.length || 0) +
                            (people?.length || 0) +
                            (tickets?.length || 0)
                        );
                      }}
                      className={classes.hover}
                    />
                  </div>
                }
                {...a11yProps(idx + STATIC_TABS.length)}
              />
            ))}
          </AntTabs>
        </AppBar>
        {value <= 3 && (
          <DashboardNavBar
            setPausePolling={setPausePolling}
            setTab={setValue}
            tab={value}
            openChat={handleOpenChat}
            openCore={handleOpenCore}
            chatWindowRef={chatWindowRef}
            setPopper={setPopper}
            showRadioLog={true}
            handleIncidentOpening={handleIncidentOpening}
            handlePersonOpening={handlePersonOpening}
            handleTicketOpening={handleTicketOpening}
            handleVehicleOpening={handleVehicleOpening}
            getCallInfo={getCallInfo}
            radioLogIncidents={value < 1 ? activeIncidents : []}
            currentVersionDate={props.currentVersionDate}
            userPreferences={userPreferences}
            setUserPreferences={setUserPreferences}
            dashboardNavBarRef={dashboardNavBarRef}
            createNewIncident={throttledCreateNewIncident}
            subscriptionServer={subscriptionServer}
            socketConnected={socketConnected}
          />
        )}
        <IncidentSubscriptionContextProvider>
          <TabPanel value={value} index={0}>
            <Dashboard
              handleIncidentOpening={handleIncidentOpening}
              handlePersonOpening={handlePersonOpening}
              handleTicketOpening={handleTicketOpening}
              setPopper={setPopper}
              pausePolling={pausePolling}
              currentVersionDate={props.currentVersionDate}
              bulletinOpen={bulletinToggle}
              bulletinSize={bulletinSize}
              userPreferences={userPreferences}
              timestamp={timestamp}
            />
          </TabPanel>

          <TabPanel value={value} index={1}>
            <WorkQueue
              tableHeight={tabWrapperRef?.current?.offsetHeight * 0.9}
              dashSize={dashSize}
              handleIncidentOpening={handleIncidentOpening}
              handlePersonOpening={handlePersonOpening}
              handleTicketOpening={handleTicketOpening}
              officerIncidents={officerWorkQueueData?.officerWorkQueue}
              officerWorkQueueLoading={officerWorkQueueLoading}
              officerWorkQueueError={officerWorkQueueError}
              supervisorIncidents={supervisorWorkQueueData?.supervisorWorkQueue}
              supervisorWorkQueueLoading={supervisorWorkQueueLoading}
              supervisorWorkQueueError={supervisorWorkQueueError}
            />
          </TabPanel>
          <TabPanel value={value} index={2}>
            <QueryReturnTabWrapper
              handleIncidentOpening={handleIncidentOpening}
              handlePersonOpening={handlePersonOpening}
              handleTicketOpening={handleTicketOpening}
              subscriptionServer={subscriptionServer}
              dashboardNavBarRef={dashboardNavBarRef}
              tabsRef={tabsRef}
            />
          </TabPanel>

          <TabPanel value={value} index={3}>
            <LogsTabWrapper handleIncidentOpening={handleIncidentOpening} />
          </TabPanel>

          {incidents.map((i, idx) => {
            return (
              <SingleIncidentProvider
                key={i.id}
                incident={i}
                user={user}
                updatePis={updatePis}
              >
                <TabPanel
                  value={value}
                  index={idx + STATIC_TABS.length}
                  key={idx + STATIC_TABS.length}
                >
                  <IncidentPanelsWrapper
                    setPopper={setPopper}
                    chatWindowRef={chatWindowRef}
                    openChat={handleOpenChat}
                    subscriptionServer={subscriptionServer}
                    incident={i}
                    target={idx + 1}
                    incidentIndex={idx}
                    handlePersonOpening={handlePersonOpening}
                    handleIncidentOpening={handleIncidentOpening}
                    handleTicketOpening={handleTicketOpening}
                    handleVehicleOpening={handleVehicleOpening}
                    getCallInfo={getCallInfoExpandFrame}
                    setPausePolling={setPausePolling}
                    userPreferences={userPreferences}
                    setUserPreferences={setUserPreferences}
                    userId={user?.id}
                    socketConnected={socketConnected}
                  />
                </TabPanel>
              </SingleIncidentProvider>
            );
          })}
          {people.map((person, idx) => {
            return (
              <TabPanel
                value={value}
                index={idx + STATIC_TABS.length + (incidents?.length || 0)}
                key={idx + STATIC_TABS.length + (incidents?.length || 0)}
              >
                <PersonDetailWrapper
                  key={person?.id}
                  pis={{ person: person }}
                  pisIndex={null}
                  personIndex={idx}
                  incidentId={null}
                  incidentUuid={null}
                  incidentIndex={null}
                  incidentCallTime={null}
                  personTab={null}
                  setPersonTab={() => alert("no function")}
                  peopleLength={1}
                  incidentOriId={null}
                  inIncident={false}
                  userRoles={user?.roleNames}
                  handlePersonOpening={handlePersonOpening}
                  handleIncidentOpening={handleIncidentOpening}
                  handleTicketOpening={handleTicketOpening}
                  canEdit={checkPermission(
                    user?.roleNames,
                    "edit_incident",
                    "ALL",
                    null,
                    null
                  )}
                  createNewIncident={throttledCreateNewIncident}
                  setPausePolling={setPausePolling}
                  setPopper={setPopper}
                />
              </TabPanel>
            );
          })}
          {tickets.map((ticket, idx) => {
            const hasBlankTicket = tickets.some(
              ticket =>
                !ticket?.ticketViolationPrototype?.id ||
                !ticket?.ticketViolationPrototype?.fullName
            );
            return (
              <TabPanel
                value={value}
                index={
                  idx +
                  STATIC_TABS.length +
                  (incidents?.length || 0) +
                  (people?.length || 0)
                }
                key={
                  idx +
                  STATIC_TABS.length +
                  (incidents?.length || 0) +
                  (people?.length || 0)
                }
              >
                <TicketAndWarning
                  incidentIndex={null}
                  incidentId={ticket?.incident?.id}
                  incidentUuid={ticket?.incident?.id}
                  ticket={ticket}
                  ticketsLoading={false}
                  ticketsError={null}
                  piss={ticket?.incident?.personIncidentSpecifics}
                  incidentPeople={returnPeopleInIncident(
                    ticket?.incident?.personIncidentSpecifics
                  )}
                  incidentOfficers={returnOfficersInIncident(
                    ticket?.incident?.officerIncidentSpecifics
                  )}
                  viss={ticket?.incident?.vehicleIncidentSpecifics}
                  ticketTab={null}
                  setTicketTab={() => alert("no function")}
                  ticketsLength={1}
                  incidentOriId={ticket?.incident?.ori?.id}
                  primaryOfficer={findPrimaryOfficer(
                    ticket?.incident?.officerIncidentSpecifics
                  )}
                  inIncident={false}
                  canEdit={checkPermission(
                    user?.roleNames,
                    "edit_incident",
                    user?.editableOriIds,
                    ticket?.incident?.ori?.id,
                    ticket?.incident?.id
                  )}
                  userRoles={user?.roleNames}
                  hasBlankTicket={hasBlankTicket}
                />
                {/* Test */}
              </TabPanel>
            );
          })}
          {vehicles.map((vis, idx) => {
            return (
              <TabPanel
                value={value}
                index={
                  idx +
                  STATIC_TABS.length +
                  (incidents?.length || 0) +
                  (people?.length || 0) +
                  (tickets?.length || 0)
                }
                key={
                  idx +
                  STATIC_TABS.length +
                  (incidents?.length || 0) +
                  (people?.length || 0) +
                  (tickets?.length || 0)
                }
              >
                <VehicleDetail
                  disabled={true}
                  inIncident={false}
                  canEdit={false}
                  userRoles={null}
                  vis={vis}
                  visIndex={idx}
                  incidentId={null}
                  incidentUuid={null}
                  incidentIndex={null}
                  piss={null}
                  vissLength={1}
                  ticketTab={null}
                  incidentOriId={vis?.incident?.ori?.id}
                  setTicketTab={() =>
                    alert("no function passed to Vehicle Detail")
                  }
                  createNewIncident={throttledCreateNewIncident}
                  setPopper={setPopper}
                />
              </TabPanel>
            );
          })}
        </IncidentSubscriptionContextProvider>
      </div>
      <DispatchedModal
        open={dispatched?.value}
        close={() => setDispatched({ value: false, incidentId: null })}
        incidentId={dispatched?.incidentId}
        userId={user?.id}
        handleIncidentOpening={handleIncidentOpening}
      />
    </div>
  );
}
